import type { ResourceThemeWithResources } from "@dune/types";
import { Button, DataTable, Input } from "@dune/ui";
import { resourceTypeLabels } from "@dune/vars";
import type { ResourceType } from "@dune/vars";
import { Plus } from "lucide-react";
import { useState } from "react";
import {
	useCreateResourceThemeMutation,
	useDeleteResourceThemeMutation,
} from "../../../api/mutations/resources.mutations";
import { useGetResourceThemes } from "../../../api/queries/resources.queries";
import ConfirmationDialog from "../../../component/common/ConfirmationDialog";
import { getResourceThemesColumns } from "./resourceThemesColumns";

const managedResourceTypes: ResourceType[] = ["DOCUMENT"];

interface ResourceThemeColumnProps {
	resourceType: ResourceType;
}

const ResourceThemeColumn = ({ resourceType }: ResourceThemeColumnProps) => {
	const [newThemeName, setNewThemeName] = useState("");
	const [themeToDelete, setThemeToDelete] = useState<ResourceThemeWithResources | null>(
		null,
	);
	const { data: themes = [], isLoading } = useGetResourceThemes({
		type: resourceType,
	});
	const createThemeMutation = useCreateResourceThemeMutation();
	const deleteThemeMutation = useDeleteResourceThemeMutation();

	const handleCreateTheme = () => {
		const trimmedName = newThemeName.trim();
		if (!trimmedName) {
			return;
		}
		createThemeMutation.mutate(
			{
				type: resourceType,
				name: trimmedName,
			},
			{
				onSuccess: () => {
					setNewThemeName("");
				},
			},
		);
	};

	const handleDeleteTheme = async () => {
		if (!themeToDelete) {
			return;
		}
		await deleteThemeMutation.mutateAsync(themeToDelete.id);
		setThemeToDelete(null);
	};

	const columns = getResourceThemesColumns({
		onDelete: (theme) => {
			setThemeToDelete(theme);
		},
	});

	return (
		<div className="flex flex-col gap-4 w-[30%] min-w-[300px] flex-shrink-0 border rounded-lg bg-white">
			<div className="px-4 py-3 border-b">
				<h3 className="text-base font-semibold">{resourceTypeLabels[resourceType]}</h3>
			</div>

			<div className="p-4 space-y-4">
				<div className="flex items-center gap-2">
					<Input
						value={newThemeName}
						onChange={(event) => setNewThemeName(event.target.value)}
						placeholder="Ajouter un thème"
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								handleCreateTheme();
							}
						}}
					/>
					<Button
						type="button"
						onClick={handleCreateTheme}
						disabled={createThemeMutation.isPending || !newThemeName.trim()}
						isLoading={createThemeMutation.isPending}
					>
						<Plus className="h-4 w-4" />
						Ajouter
					</Button>
				</div>

				<DataTable
					columns={columns}
					data={themes}
					isLoading={isLoading}
					noResultsText="Aucun thème pour ce type"
					variant="static-clean"
				/>
			</div>

			<ConfirmationDialog
				open={Boolean(themeToDelete)}
				onOpenChange={(open) => {
					if (!open) {
						setThemeToDelete(null);
					}
				}}
				title={
					themeToDelete && themeToDelete.resources.length > 0
						? "Suppression impossible"
						: "Supprimer le thème"
				}
				description={
					themeToDelete
						? themeToDelete.resources.length > 0
							? `Impossible de supprimer "${themeToDelete.name}" tant que des ressources y sont liées : ${themeToDelete.resources.map((resource) => resource.name).join(", ")}`
							: `Voulez-vous supprimer le thème "${themeToDelete.name}" ?`
						: ""
				}
				confirmText={
					themeToDelete && themeToDelete.resources.length > 0
						? "Fermer"
						: "Supprimer"
				}
				cancelText="Annuler"
				variant={
					themeToDelete && themeToDelete.resources.length > 0
						? "warning"
						: "danger"
				}
				onConfirm={
					themeToDelete && themeToDelete.resources.length > 0
						? () => setThemeToDelete(null)
						: handleDeleteTheme
				}
				isLoading={
					themeToDelete && themeToDelete.resources.length > 0
						? false
						: deleteThemeMutation.isPending
				}
			/>
		</div>
	);
};

export const ResourceThemesTab = () => {
	return (
		<div className="flex flex-wrap gap-4">
			{managedResourceTypes.map((resourceType) => (
				<ResourceThemeColumn key={resourceType} resourceType={resourceType} />
			))}
		</div>
	);
};
